import { defineStore } from "pinia";
import axios from "axios";
import { calendarEventsUrl } from "@/services/api";
import type { CalendarEventDTO, CalendarEventQuery } from "@/types/calendar";

export const useCalendarStore = defineStore("calendar", {
    state: () => ({
        events: [] as CalendarEventDTO[],
        loading: false as boolean,
        lastQuery: null as CalendarEventQuery | null,
    }),
    getters: {
        isLoading: (state) => state.loading,
        getEvents: (state) => state.events,
    },
    actions: {
        async fetchEvents(range: CalendarEventQuery) {
            this.loading = true;
            try {
                const response = await axios.get(calendarEventsUrl, {
                    params: range,
                });
                if (response.status !== 200) {
                    throw new Error(`Unexpected status code ${response.status}`);
                }
                this.events = (response.data || []) as CalendarEventDTO[];
                this.lastQuery = range;
                return this.events;
            } catch (error) {
                console.error("Failed to load calendar events:", error);
                this.events = [];
                throw error;
            } finally {
                this.loading = false;
            }
        },
        async updateEvent(eventId: number, payload: Partial<CalendarEventDTO>) {
            this.loading = true;
            try {
                const response = await axios.put(`${calendarEventsUrl}${eventId}/`, payload);
                if (response.status !== 200) {
                    throw new Error(`Unexpected status code ${response.status}`);
                }
                const updated = response.data as CalendarEventDTO;
                this.events = this.events.map((event) => (event.id === updated.id ? updated : event));
                return updated;
            } catch (error) {
                console.error("Failed to update calendar event:", error);
                throw error;
            } finally {
                this.loading = false;
            }
        },
        async createEvent(payload: Partial<CalendarEventDTO>) {
            this.loading = true;
            try {
                const response = await axios.post(calendarEventsUrl, payload);
                if (response.status !== 201 && response.status !== 200) {
                    throw new Error(`Unexpected status code ${response.status}`);
                }
                const created = response.data as CalendarEventDTO;
                this.events.push(created);
                return created;
            } catch (error) {
                console.error("Failed to create calendar event:", error);
                throw error;
            } finally {
                this.loading = false;
            }
        },
    },
});
